import { LogManager } from '@src/utility/Logger';
import { v4 as uuid } from 'uuid';

import { createCaptionLineBreak } from '../../models/karaoke/CaptionLineBreak';
import { KaraokeClip } from '../../models/karaoke/KaraokeClip';
import { splitPhraseSoftBreaks } from '../../services/captionSoftBreaks';
import { joinCaptionRowTexts } from '../../services/karaokeSegmentation';
import {
  computeProportionAtTextPosition,
  DEFAULT_SPLIT_STRATEGY,
} from '../../services/karaokeWordTiming';
import { AbstractCommand } from '../AbstractCommand';

import type { CaptionLineBreak } from '../../models/karaoke/CaptionLineBreak';
import type { VideoEditor } from '../../VideoEditor';

const logger = LogManager.getLogger('karaoke:syntheticBreak');

/**
 * PLAT-9623: insert a synthetic line break at `textPosition` inside the
 * single-phrase translated clip identified by `clipId`.
 *
 * Time placement is derived from `textPosition` via the active
 * `SplitStrategy` in `applySyntheticBreaksToPhrase`. The single clip
 * becomes N sub-clips (one per resulting sub-unit), all sharing the
 * original source tokens; only `startSecondsOffset` / `endSecondsOffset`
 * vary so each sub-clip spans its own time window.
 *
 * Refuses to operate on multi-phrase clips — those don't have a single
 * `phraseOriginalIndices` identity. Multi-phrase synthetic-break
 * support is a follow-up.
 */
export class AddSyntheticCaptionBreakCommand extends AbstractCommand {
  private clipId: string;
  private phraseOriginalIndices: number[];
  private textPosition: number;

  private prevClips: KaraokeClip[] | null = null;
  private prevBreaks: CaptionLineBreak[] | null = null;
  private addedBreakId: string | null = null;

  constructor(
    editor: VideoEditor,
    clipId: string,
    phraseOriginalIndices: number[],
    textPosition: number,
  ) {
    super(editor);
    this.clipId = clipId;
    this.phraseOriginalIndices = phraseOriginalIndices;
    this.textPosition = textPosition;
  }

  getName(): string {
    return 'AddSyntheticCaptionBreakCommand';
  }

  getDescription(): string {
    return `Add synthetic caption break at text position ${this.textPosition}`;
  }

  execute(): boolean {
    const track = this.editor.getKaraokeTrack();
    if (!track) {
      logger.debug('execute: no karaoke track');
      return false;
    }

    const clipIndex = track.karaokeClips.findIndex(c => c.id === this.clipId);
    if (clipIndex < 0) {
      logger.warn('execute: clip not found', { clipId: this.clipId });
      return false;
    }
    const clip = track.karaokeClips[clipIndex];

    if (!clip.translatedPhrases || clip.translatedPhrases.length === 0) {
      logger.warn('execute: clip has no translatedPhrases', {
        clipId: this.clipId,
      });
      return false;
    }

    // Walk the joined translatedText (phrases joined by single spaces)
    // to locate the cursor's textPosition: which phrase contains it
    // and where within that phrase. textPosition is line-relative
    // (matches the cursor-position model in TranslatedCaptionsView).
    let charsConsumed = 0;
    let targetPhraseIdx = -1;
    let phraseRelativePos = -1;
    for (let i = 0; i < clip.translatedPhrases.length; i++) {
      const phrase = clip.translatedPhrases[i];
      const phraseEnd = charsConsumed + phrase.text.length;
      if (
        this.textPosition >= charsConsumed &&
        this.textPosition <= phraseEnd
      ) {
        targetPhraseIdx = i;
        phraseRelativePos = this.textPosition - charsConsumed;
        break;
      }
      charsConsumed = phraseEnd + 1; // joining space
    }
    if (targetPhraseIdx < 0) {
      logger.warn('execute: textPosition out of clip range', {
        textPosition: this.textPosition,
      });
      return false;
    }

    const targetPhrase = clip.translatedPhrases[targetPhraseIdx];
    const origStart = clip.translatedPhrases[0].ts;
    const origEnd =
      clip.translatedPhrases[clip.translatedPhrases.length - 1].endTs;

    // Build leftPhrases / rightPhrases. Character-weighted timing
    // applies ONLY when the split lands inside a phrase (a synthetic
    // split within one contiguous source segment). At a natural
    // phrase boundary we keep each phrase's source ts/endTs intact —
    // those represent real upstream word timing for the source
    // language and shouldn't be reflowed.
    let leftPhrases: {
      text: string;
      ts: number;
      endTs: number;
      originalIndices?: number[];
    }[];
    let rightPhrases: {
      text: string;
      ts: number;
      endTs: number;
      originalIndices?: number[];
    }[];
    // PLAT-11914: where the right clip starts, to rebase soft line breaks.
    let softSplit = { phraseIndex: targetPhraseIdx, offset: 0 };
    let softSplitTrim = 0;

    if (phraseRelativePos === 0) {
      // Split AT the start of phrase[targetPhraseIdx] — clean
      // phrase-boundary split. Phrases [0..idx-1] go left, [idx..]
      // go right. Original phrase ts/endTs preserved on both sides.
      if (targetPhraseIdx === 0) {
        return false;
      }
      leftPhrases = clip.translatedPhrases.slice(0, targetPhraseIdx);
      rightPhrases = clip.translatedPhrases.slice(targetPhraseIdx);
    } else if (phraseRelativePos === targetPhrase.text.length) {
      // Split AT the end of phrase[targetPhraseIdx] — clean
      // phrase-boundary split. Phrases [0..idx] go left, [idx+1..]
      // go right. Original phrase ts/endTs preserved on both sides.
      if (targetPhraseIdx === clip.translatedPhrases.length - 1) {
        return false;
      }
      leftPhrases = clip.translatedPhrases.slice(0, targetPhraseIdx + 1);
      rightPhrases = clip.translatedPhrases.slice(targetPhraseIdx + 1);
      softSplit = { phraseIndex: targetPhraseIdx + 1, offset: 0 };
    } else {
      // Synthetic in-phrase split: cut the target phrase's text at
      // `phraseRelativePos`. The split time is character-weighted
      // WITHIN this single phrase's `[ts, endTs]` window via the
      // shared `computeProportionAtTextPosition` strategy
      // (`karaokeWordTiming.ts`) — same algorithm word placement
      // uses, so per-word highlight crosses the new break exactly
      // when it crosses the underlying text position. Other phrases
      // in the clip are NOT touched.
      const proportion = computeProportionAtTextPosition(
        targetPhrase.text,
        phraseRelativePos,
        DEFAULT_SPLIT_STRATEGY,
      );
      if (proportion <= 0 || proportion >= 1) {
        return false;
      }
      const splitTs =
        targetPhrase.ts + proportion * (targetPhrase.endTs - targetPhrase.ts);
      const leftText = targetPhrase.text.slice(0, phraseRelativePos).trimEnd();
      const rightText = targetPhrase.text.slice(phraseRelativePos).trimStart();
      softSplit = { phraseIndex: targetPhraseIdx, offset: phraseRelativePos };
      softSplitTrim =
        targetPhrase.text.length - phraseRelativePos - rightText.length;
      if (leftText.length === 0 || rightText.length === 0) {
        return false;
      }
      // Both halves of an in-phrase split inherit the original
      // phrase's `originalIndices` — they're still pieces of the
      // SAME source phrase, just split visually. The captions view
      // uses this to light all halves together on hover.
      const inheritedIndices = targetPhrase.originalIndices
        ? [...targetPhrase.originalIndices]
        : undefined;
      leftPhrases = [
        ...clip.translatedPhrases.slice(0, targetPhraseIdx),
        {
          text: leftText,
          ts: targetPhrase.ts,
          endTs: splitTs,
          originalIndices: inheritedIndices,
        },
      ];
      rightPhrases = [
        {
          text: rightText,
          ts: splitTs,
          endTs: targetPhrase.endTs,
          originalIndices: inheritedIndices,
        },
        ...clip.translatedPhrases.slice(targetPhraseIdx + 1),
      ];
    }

    // Capture undo state AFTER all refusal paths.
    this.prevClips = [...track.karaokeClips];
    this.prevBreaks = [...track.syntheticBreaks];

    // For single-phrase clips we keep tracking a CaptionLineBreak
    // record so the split survives a re-produce. Phrase identity
    // resolves from the clip when present, falling back to the
    // caller's hint (Tier 2 derived translations stamp it on the
    // line). Multi-phrase clips don't have a stable phrase identity
    // for the record — the split is durable in the new clip
    // structure but won't survive a re-produce; that's acceptable
    // for the MVP and covers the user's immediate case.
    const isSinglePhrase = clip.translatedPhrases.length === 1;
    const phraseIdentity =
      clip.phraseOriginalIndices && clip.phraseOriginalIndices.length > 0
        ? clip.phraseOriginalIndices
        : this.phraseOriginalIndices;
    let record: CaptionLineBreak | null = null;
    if (isSinglePhrase && phraseIdentity && phraseIdentity.length > 0) {
      record = createCaptionLineBreak({
        phraseOriginalIndices: phraseIdentity,
        textPosition: this.textPosition,
      });
      this.addedBreakId = record.id;
    }

    const softBreaks = splitPhraseSoftBreaks(
      clip.softBreakPhrasePositions ?? [],
      softSplit,
      softSplitTrim,
    );

    const leftEndTs = leftPhrases[leftPhrases.length - 1].endTs;
    const rightStartTs = rightPhrases[0].ts;

    // Sibling marker: both halves share an id. Inherit from the
    // parent if it had one (= part of an existing sibling group),
    // else mint a fresh one. This survives any line-array
    // gymnastics (formatter rebuild, spread, etc.) that strip
    // reference identity from `transcriptTokens`.
    const siblingGroupId = clip.siblingGroupId ?? uuid();

    // Offset math: the clip's `startSecondsOffset` / `endSecondsOffset`
    // are RELATIVE TO THE TOKEN-RANGE START/END (`tokens[0].ts` /
    // `tokens[last].endTs`), not the clip's effective start/end. When
    // we split a clip whose parent was itself the result of a previous
    // split, the parent ALREADY has non-zero offsets shifting it off
    // the token-range edges. The new sub-clips inherit the same token
    // array, so the parent's offsets MUST be carried into the sub-clip
    // offsets — otherwise the math is computed against the wrong
    // anchor and accumulated error compounds with each split,
    // eventually pushing the last sub-clip into a near-zero or
    // negative duration (= invisible on screen).
    const parentStartOffset = clip.startSecondsOffset ?? 0;
    const parentEndOffset = clip.endSecondsOffset ?? 0;

    const leftClip = new KaraokeClip({
      id: clip.id,
      transcriptTokens: clip.transcriptTokens,
      hiddenTranscriptTokenIds: [...clip.hiddenTranscriptTokenIds],
      ignoredByFiltersIds: [...clip.ignoredByFiltersIds],
      karaokeConfig: clip.karaokeConfig,
      track: clip.track,
      translatedText: joinCaptionRowTexts(leftPhrases.map(p => p.text)),
      translatedPhrases: leftPhrases,
      phraseOriginalIndices:
        leftPhrases.length === 1 && phraseIdentity ? phraseIdentity : undefined,
      siblingGroupId,
      softBreakPhrasePositions: softBreaks.left,
      // Left clip starts where the parent did → inherit verbatim.
      startSecondsOffset: parentStartOffset,
      // Left clip ends at leftEndTs. Anchor the offset on the token
      // range end (= parent effective end - parentEndOffset). So the
      // delta to leftEndTs is `(leftEndTs - origEnd) + parentEndOffset`.
      endSecondsOffset: leftEndTs - origEnd + parentEndOffset,
    });
    const rightClip = new KaraokeClip({
      id: uuid(),
      transcriptTokens: clip.transcriptTokens,
      hiddenTranscriptTokenIds: [...clip.hiddenTranscriptTokenIds],
      ignoredByFiltersIds: [...clip.ignoredByFiltersIds],
      karaokeConfig: clip.karaokeConfig,
      track: clip.track,
      translatedText: joinCaptionRowTexts(rightPhrases.map(p => p.text)),
      translatedPhrases: rightPhrases,
      phraseOriginalIndices:
        rightPhrases.length === 1 && phraseIdentity
          ? phraseIdentity
          : undefined,
      siblingGroupId,
      softBreakPhrasePositions: softBreaks.right,
      // Right clip starts at rightStartTs. Same anchor logic as the
      // left clip's endSecondsOffset, in reverse.
      startSecondsOffset: rightStartTs - origStart + parentStartOffset,
      // Right clip ends where the parent did → inherit verbatim.
      endSecondsOffset: parentEndOffset,
    });

    track.karaokeClips = [
      ...track.karaokeClips.slice(0, clipIndex),
      leftClip,
      rightClip,
      ...track.karaokeClips.slice(clipIndex + 1),
    ];
    if (record) {
      track.syntheticBreaks = [...this.prevBreaks, record];
    }

    this.editor.markDirty('synthetic_caption_break_added');
    return true;
  }

  undo(): boolean {
    if (!this.prevClips || !this.prevBreaks) return false;
    const track = this.editor.getKaraokeTrack();
    if (!track) return false;
    track.karaokeClips = this.prevClips;
    track.syntheticBreaks = this.prevBreaks;
    this.editor.markDirty('synthetic_caption_break_added_undo');
    return true;
  }

  /** For tests / panel wiring — id of the break added by this execution. */
  getAddedBreakId(): string | null {
    return this.addedBreakId;
  }
}
